import type { QueryClient } from "@tanstack/react-query";

import {
  getFilterOptionsQueryKey,
  getGetOrgComplianceSettingsQueryKey,
} from "@/lib/api/generated";
import { SAVED_SEARCH_QUERY_PREFIX } from "@/hooks/use-search";
import type { MeResponse } from "@/lib/api/model/meResponse";
import type { OrgComplianceSettingsResponse } from "@/lib/api/model/orgComplianceSettingsResponse";

/** Refetch server data that embeds org privacy presentation (names, photos, highlights). */
export function invalidateCompliancePresentationQueries(
  queryClient: QueryClient,
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: getGetOrgComplianceSettingsQueryKey(),
    }),
    queryClient.invalidateQueries({ queryKey: ["auth"] }),
    queryClient.invalidateQueries({ queryKey: ["profile"] }),
    queryClient.invalidateQueries({ queryKey: ["candidate"] }),
    queryClient.invalidateQueries({ queryKey: SAVED_SEARCH_QUERY_PREFIX }),
    queryClient.invalidateQueries({ queryKey: ["search"] }),
    queryClient.invalidateQueries({ queryKey: ["search-insights"] }),
    queryClient.invalidateQueries({ queryKey: getFilterOptionsQueryKey() }),
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0];
        if (typeof firstKey !== "string") return false;
        return [
          "/api/searches/",
          "/api/jobs/",
          "/api/arbitrator/",
          "/api/shares/",
          "/api/tasks",
          "/api/exclusions/people",
        ].some((prefix) => firstKey.startsWith(prefix));
      },
    }),
  ]).then(() => undefined);
}

export function patchAuthComplianceSettings(
  queryClient: QueryClient,
  settings: OrgComplianceSettingsResponse,
): void {
  queryClient.setQueryData<MeResponse | undefined>(["auth", "me"], (current) =>
    current ? { ...current, compliance_settings: settings } : current,
  );
}
